import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { seo } from "../translations/seo";

const LOGO_URL = "/images/logo.png";

export default function Footer() {
  const { t, language } = useLanguage();

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <div className="inline-block bg-white rounded-xl p-2 mb-4">
              <img
                src={LOGO_URL}
                alt="Psymed"
                className="h-10 w-auto object-contain"
              />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              {t.footer.tagline}
            </p>
            <div className="mt-4 p-3 bg-white/5 rounded-xl border border-white/10">
              <p className="text-xs text-slate-400">{t.footer.professional}</p>
              <a
                href="#contact"
                className="mt-2 flex items-center gap-1 text-[#3B9DA5] text-xs font-semibold hover:text-white transition-colors"
              >
                {t.nav.contact} <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-outfit font-semibold text-white mb-5 uppercase tracking-wide text-sm">
              {t.footer.quick_links}
            </h3>
            <ul className="space-y-3">
              {[
                { label: t.nav.home, href: "/" },
                { label: t.nav.services, href: "/#services" },
                { label: t.nav.team, href: "/equipe" },
                { label: t.nav.blog, href: "/actualites" },
                { label: t.collaborer.title, href: "/collaborez" },
                { label: t.nav.contact, href: "/#contact" },
                { label: t.footer.privacy, href: "/confidentialite" },
                { label: seo[language].legal.title, href: "/mentions-legales" },
              ].map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-slate-400 hover:text-white text-sm transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 bg-[#3B9DA5] rounded-full group-hover:w-2 transition-all" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-outfit font-semibold text-white mb-5 uppercase tracking-wide text-sm">
              {t.centres.title}
            </h3>
            <div className="space-y-5">
              <div>
                <Link to="/centres/cinquantenaire" className="flex items-start gap-3 group">
                  <MapPin className="w-4 h-4 text-[#3B9DA5] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-white font-semibold group-hover:text-[#8ecdd4] transition-colors">Psymed Cinquantenaire</p>
                    <p className="text-xs text-slate-400">Avenue de la Renaissance 21</p>
                    <p className="text-xs text-slate-400">1000 Bruxelles</p>
                  </div>
                </Link>
              </div>
              <div>
                <Link to="/centres/abbaye" className="flex items-start gap-3 group">
                  <MapPin className="w-4 h-4 text-[#3B9DA5] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-white font-semibold group-hover:text-[#8ecdd4] transition-colors">Psymed Abbaye</p>
                    <p className="text-xs text-slate-400">Avenue de l'Hippodrome 117</p>
                    <p className="text-xs text-slate-400">1050 Ixelles</p>
                  </div>
                </Link>
              </div>
              <Link
                to="/centres"
                className="inline-flex items-center gap-2 text-[#3B9DA5] text-sm font-medium hover:text-white transition-colors"
              >
                {t.centres.all_centres} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-slate-500 text-xs">
            &copy; {new Date().getFullYear()} Centre Psymed — Benedetta Branchesi, BCE 0663580661. {t.footer.rights}.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/mentions-legales" className="text-slate-500 hover:text-slate-300 text-xs transition-colors" data-testid="footer-legal-link">
              {seo[language].legal.title}
            </Link>
            <Link
              to="/confidentialite"
              className="text-slate-500 hover:text-slate-300 text-xs transition-colors"
            >
              {t.footer.privacy}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
